import React, { FC, useContext, useEffect } from "react";
import { useSelector } from "react-redux";
import { InputAdornment, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";

import { formatCurrency } from "../../../../../utility/utils"
import { SessionContext } from "../../../../../utility/useSession";
import { PaymentReconciliation, PaymentTypes, ProductSaleItem } from "../../../../../logic/api";
import flex from "../../../../../styles/flex";

import { RootState, useAppDispatch } from "../../../../../state/store";
import { initializeReconciledSaleItems, updateReconciledSaleItems } from "../../../../../state/reducers/paymentsReducer";
import MoneyInputField, { UpdateOn } from "../../../../../components/editable/EditableField/fields/MoneyInputField";
import { useTranslation } from "react-i18next";
import { ReleaseFlag, useReleaseManagement } from "@/utility/feature-management";

interface IPartialPayment {
  saleItems: ProductSaleItem[],
  reconciliations: PaymentReconciliation[]
}

export interface ReconciledSaleItem {
  maxRefund: number,
  payment: number,
  productCode: string,
  productSaleItemId: number,
  name: string,
  remainingQuantity: number,
  rate: number,
  outstandingAmount: number,
  total: number,
}

const PartialPayment: FC<IPartialPayment> = ({
  saleItems,
  reconciliations
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const paymentType = useSelector((state: RootState) => state.payments.paymentType);
  const reconciledSaleItems = useSelector((state: RootState) => state.payments.reconciledSaleItems);

  const session = useContext(SessionContext);
  const currencySymbol = session.current.site?.preferences.currency;

  const isFloatingPointPaymentFixEnabled = useReleaseManagement(
    ReleaseFlag.releaseFloatingPointPaymentFix
  );


  useEffect(() => {
    dispatch(initializeReconciledSaleItems({ saleItems, reconciliations, floatingPointFixEnabled: isFloatingPointPaymentFixEnabled }));
    initializePaymentRowValues();
  }, []);

  useEffect(() => {
    initializePaymentRowValues();
  }, [paymentType]);

  const initializePaymentRowValues = () => {
    dispatch(updateReconciledSaleItems({
      saleItems: reconciledSaleItems.map((rsi: ReconciledSaleItem) => {
        const newRsi = { ...rsi };
        newRsi.payment = isRefund ? rsi.maxRefund : rsi.outstandingAmount;
        return newRsi;
      }),
      floatingPointFixEnabled: isFloatingPointPaymentFixEnabled
    }));
  }

  const updateReconciledSaleItemsPayment = (payment: number, saleItemId: number) => {
    const newReconciledSaleItems = JSON.parse(JSON.stringify(reconciledSaleItems));

    const rsiToUpdate = newReconciledSaleItems.find((rsi: ReconciledSaleItem) => rsi.productSaleItemId === saleItemId);

    if (rsiToUpdate) {
      rsiToUpdate.payment = payment;
    }

    return newReconciledSaleItems;
  }

  const isItemPaymentValid = (rsi: ReconciledSaleItem) => {
    if (isRefund) {
      return (0 <= rsi.payment && rsi.payment <= rsi.total - rsi.outstandingAmount);
    }

    return (0 <= rsi.payment && rsi.payment <= rsi.outstandingAmount);
  }

  const isRefund = paymentType === PaymentTypes.refund;

  //cell horizontal padding is 16 as per default, so we're working with 360 width total for the cells.

  return <Table sx={{ maxWidth: 520 }} size="small">
    <TableHead>
      <TableRow>
        <TableCell sx={{ width: 60, fontWeight: 700 }}>{t("booking.labels.product")}</TableCell>
        <TableCell sx={{ width: 40, fontWeight: 700 }}>{isRefund ? " " : t("payments.qty")}</TableCell>
        <TableCell sx={{ width: 50, fontWeight: 700 }}>{isRefund ? " " : t("payments.rate")}</TableCell>
        <TableCell sx={{ width: 100, fontWeight: 700 }}>{paymentType === PaymentTypes.refund ? t("payments.maxRefund") : t("payments.outstanding")}</TableCell>
        <TableCell sx={{ width: 110, fontWeight: 700 }}>&nbsp; &nbsp; &nbsp;{t("payments.payment")}</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {reconciledSaleItems.map((rsi: ReconciledSaleItem, index: number) => {
        if (!isRefund && rsi.outstandingAmount || isRefund && rsi.maxRefund) {
          return (<TableRow key={`partialPaymentRow-${rsi.productCode}-${rsi.productSaleItemId}`}>
            <TableCell>{rsi.name}</TableCell>
            <TableCell>{isRefund ? " " : rsi.remainingQuantity}</TableCell>
            <TableCell>{isRefund ? " " : formatCurrency(rsi.rate, session)}</TableCell>
            <TableCell>{isRefund ? formatCurrency(rsi.maxRefund, session) : formatCurrency(rsi.outstandingAmount, session)}</TableCell>
            <TableCell>
              <div style={{ ...flex.flexRows, width: 100, height: 28, marginTop: -4 }}>
                <div>
                  <MoneyInputField
                    label=""
                    value={reconciledSaleItems[index].payment}
                    updateOn={UpdateOn.Change}
                    error={!isItemPaymentValid(rsi)}
                    inputProps={{
                      startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                      style: {
                        height: '26px',
                        fontSize: '0.875rem',
                      }
                    }}
                    onChange={(value) => {
                      const roundedValue = value.toFixed(2);
                      const newReconciledSaleItems = updateReconciledSaleItemsPayment(Number(roundedValue), rsi.productSaleItemId);
                      dispatch(updateReconciledSaleItems({ saleItems: newReconciledSaleItems, floatingPointFixEnabled: isFloatingPointPaymentFixEnabled }));
                    }}
                  ></MoneyInputField>
                </div>
              </div>
            </TableCell>
          </TableRow>);
        }
      })}
    </TableBody>
  </Table>
}

export default PartialPayment;
